import { Injectable, OnDestroy } from '@angular/core';
import { EchoService } from './echo.service';
import { Store } from '@ngrx/store';
import { AppState } from '../state/app.state';
import { updateCompanyAssistantPrompt } from '../utils/update-company-assistant-prompt.util';
import { updateCompany } from '../state/report/report.actions';
import { selectCompany } from '../state/report/report.selectors';
import { updateCompanyIcpScore } from '../utils/update-company-icp-score.util';
import { PromptService } from './prompt.service';
import { selectReport } from '../state/search/search.selectors';
import { updateReport } from '../state/search/search.actions';
import { take } from 'rxjs/operators';

/**
 * Service to handle report-specific WebSocket events
 * This service manages the connection and listeners for real-time updates
 * specific to a single report view
 *
 * IMPORTANT: This service should NOT be providedIn: 'root' to avoid cross-tab contamination
 * Each report component gets its own instance
 */
@Injectable()
export class ReportWebSocketService implements OnDestroy {
  private activeChannels: Map<string, { reportId: number, listeners: any[], callback: Function }> = new Map();

  constructor(
    private echoService: EchoService,
    private store: Store<AppState>,
    private promptService: PromptService
  ) {}

  /**
   * Connect to report-specific WebSocket channel
   * Channel format: ReportChannel.{userId}.{reportId}
   * Supports multiple channels for different reports in different tabs
   *
   * @param userId - User ID
   * @param reportId - Report ID
   * @param onEventReceived - Callback to handle events in the component context
   */
  connectToReportChannel(
    userId: number,
    reportId: number,
    onEventReceived: (report: any) => void
  ): void {
    const channelName = `ReportChannel.${userId}.${reportId}`;

    // Check if already connected to this channel
    if (this.activeChannels.has(channelName)) {
      return;
    }

    // Listen for report field events
    const channel = this.echoService.getEcho().private(channelName);

    const reportFieldListener = channel.listen('.report-event-field', (report: any) => {
      // Only process if the report ID matches this channel's report
      if (report.search_id && parseInt(report.search_id, 10) === reportId) {
        console.log(`📨 [WebSocket] Report field event`, {
          reportId,
          field: report.field_name,
          hasData: !!report.data
        });
        onEventReceived(report);
      }
    });

    // Listen for search report completed events (search_status -> 3). This is the
    // reliable "fully complete" signal (CompleteSearchTaskJob always fires it).
    // NOTE: the payload carries `search_id` (not `id`), so match on that.
    const completedListener = channel.listen('.search-report-completed', (data: any) => {
      const eventReportId = data.search_id ?? data.id;
      if (eventReportId && parseInt(eventReportId, 10) === reportId) {
        console.log(`✅ [WebSocket] Report completed`, { reportId });
        onEventReceived({ ...data, eventType: 'search-report-completed' });
      }
    });

    // Listen for apollo phone number events
    const apolloPhoneListener = channel.listen('.apollo-phone-number-event', (data: any) => {
      // Only process if the report ID matches this channel's report
      const eventReportId = data.id || data.report_id;
      if (eventReportId && parseInt(eventReportId, 10) === reportId) {
        console.log(`📞 [WebSocket] Phone number event`, {
          reportId,
          phoneCount: data.phone_numbers?.length || 0
        });
        onEventReceived({ ...data, eventType: 'apollo-phone-number' });
      }
    });

    // Listen for notes summary events
    const notesSummaryListener = channel.listen('.notes-summary-event', (data: any) => {
      console.log(`📝 [WebSocket] Notes summary event received (RAW)`, {
        data,
        reportId,
        channelName
      });

      // Only process if the report ID matches this channel's report
      const eventReportId = data.search_id || data.report_id || data.id;
      console.log(`📝 [WebSocket] Notes summary event - checking reportId`, {
        eventReportId,
        expectedReportId: reportId,
        matches: eventReportId && parseInt(eventReportId, 10) === reportId
      });

      if (eventReportId && parseInt(eventReportId, 10) === reportId) {
        console.log(`📝 [WebSocket] Notes summary event - PROCESSING`, {
          reportId,
          hasPrompt: !!data.prompt,
          dataKeys: Object.keys(data)
        });
        onEventReceived({ ...data, eventType: 'notes-summary' });
      } else {
        console.warn(`📝 [WebSocket] Notes summary event - IGNORED (report ID mismatch)`, {
          eventReportId,
          expectedReportId: reportId
        });
      }
    });

    // Store the channel info
    this.activeChannels.set(channelName, {
      reportId: reportId,
      listeners: [reportFieldListener, completedListener, apolloPhoneListener, notesSummaryListener],
      callback: onEventReceived
    });

    console.log(`✅ [WebSocket] Connected`, {
      reportId,
      activeChannels: this.activeChannels.size,
      listeners: 4
    });
  }

  /**
   * Disconnect from a specific report channel
   */
  disconnectFromReportChannel(userId?: number, reportId?: number): void {
    // If specific channel provided, disconnect from that one
    if (userId && reportId) {
      const channelName = `ReportChannel.${userId}.${reportId}`;
      this.disconnectChannel(channelName);
    } else {
      // Disconnect from all channels
      console.log(`🔌 [WebSocket] Disconnecting all`, {
        count: this.activeChannels.size
      });
      for (const channelName of this.activeChannels.keys()) {
        this.disconnectChannel(channelName);
      }
    }
  }

  /**
   * Helper method to disconnect from a specific channel
   */
  private disconnectChannel(channelName: string): void {
    if (this.activeChannels.has(channelName)) {
      try {
        const channelInfo = this.activeChannels.get(channelName);
        console.log(`🔌 [WebSocket] Disconnecting`, {
          channelName,
          reportId: channelInfo?.reportId
        });
        this.echoService.getEcho().leave(channelName);
        this.activeChannels.delete(channelName);
      } catch (error) {
        console.error(`❌ [WebSocket] Disconnect error`, {
          channelName,
          error: error.message
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.disconnectFromReportChannel();
  }
}